import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { RootState } from "store"
import { ContractorThemeKey, ContractorThemes } from "constants/ContractorThemes"
import { ApiTypes, UITypes } from "types"
import { StorageKeys } from "types/enums/StorageKeys"

import { getStorageItem, removeStorageItem, setStorageItem } from "utils/localStorage"
import { AccountSettingsTabId } from "pages/account/AccountSettingsPage/config"

const getUserActiveTheme = Object.keys(ContractorThemes).includes(getStorageItem(StorageKeys.UserTheme) as string)
  ? (getStorageItem(StorageKeys.UserTheme) as ContractorThemeKey)
  : "default"
const userLogo = getStorageItem(StorageKeys.UserLogo)
const verticalMenuOpen = getStorageItem(StorageKeys.VerticalMenuOpen) !== false

export interface TempDataState {
  contractorAccountStatusOptions: UITypes.Option[] | undefined
  currentOrderNew: ApiTypes.Model.OrderNew | undefined
  contractor: UITypes.Contractor | null
  editableWarehouse: ApiTypes.Model.Warehouse | undefined
  editableUser: ApiTypes.Model.User | undefined
  additionalRequestParams: UITypes.RequestParams | null
  currentTemplateID: number | undefined
  currentZip: ApiTypes.Model.Zip | undefined
  selectedDeviceIDs: number[]
  selectedCustomerDevices: ApiTypes.Model.Device[]
  selectedDevicesForPerformanceTest: {
    isImmediate?: boolean
    devices: ApiTypes.Model.PerformanceTestDevice[]
  }
  performanceTestReport: UITypes.PerformanceTestReport | undefined
  selectedDevices: ApiTypes.Model.VendorDevice[]
  deviceHistoryLogID: number | undefined
  warrantySNs: UITypes.WarrantySNs | undefined
  modalContent: UITypes.ModalContent | undefined
  initialURL: string | undefined
  userActiveTheme: ContractorThemeKey
  userLogo: string | undefined
  selectedMessageTemplateID: number | undefined
  accountSettingsTab: AccountSettingsTabId | undefined
  verticalMenuOpen: boolean
  mobileMenuOpen: boolean
}

const initialState: TempDataState = {
  contractorAccountStatusOptions: undefined,
  currentOrderNew: undefined,
  contractor: null,
  editableWarehouse: undefined,
  editableUser: undefined,
  additionalRequestParams: null,
  currentTemplateID: undefined,
  currentZip: undefined,
  selectedDeviceIDs: [],
  selectedCustomerDevices: [],
  selectedDevicesForPerformanceTest: {
    isImmediate: false,
    devices: [],
  },
  performanceTestReport: undefined,
  selectedDevices: [],
  deviceHistoryLogID: undefined,
  warrantySNs: undefined,
  modalContent: undefined,
  initialURL: undefined,
  userActiveTheme: getUserActiveTheme || "default",
  userLogo: userLogo || undefined,
  selectedMessageTemplateID: undefined,
  accountSettingsTab: undefined,
  verticalMenuOpen: verticalMenuOpen,
  mobileMenuOpen: false,
}

export const tempDataSlise = createSlice({
  name: "tempData",
  initialState,
  reducers: {
    setContractorAccountStatusOptions: (state, action: PayloadAction<UITypes.Option[] | undefined>) => {
      state.contractorAccountStatusOptions = action.payload
    },
    setCurrentOrderNew: (state, action: PayloadAction<ApiTypes.Model.OrderNew | undefined>) => {
      state.currentOrderNew = action.payload
    },
    setContractor: (state, action: PayloadAction<UITypes.Contractor | null>) => {
      state.contractor = action.payload
    },
    setEditableWarehouse: (state, action: PayloadAction<ApiTypes.Model.Warehouse | undefined>) => {
      state.editableWarehouse = action.payload
    },
    setEditableUser: (state, action: PayloadAction<ApiTypes.Model.User | undefined>) => {
      state.editableUser = action.payload
    },
    setAdditionalRequestParams: (state, action: PayloadAction<UITypes.RequestParams | null>) => {
      if (action.payload === null) {
        state.additionalRequestParams = {}
      } else {
        state.additionalRequestParams = { ...state.additionalRequestParams, ...action.payload }
      }
    },
    setCurrentTemplateID: (state, action: PayloadAction<number | undefined>) => {
      state.currentTemplateID = action.payload
    },
    setCurrentZip: (state, action: PayloadAction<ApiTypes.Model.Zip | undefined>) => {
      state.currentZip = action.payload
    },
    setSelectedDeviceIDs: (state, action: PayloadAction<number[]>) => {
      state.selectedDeviceIDs = action.payload
    },
    setSelectedCustomerDevices: (state, action: PayloadAction<ApiTypes.Model.Device[]>) => {
      state.selectedCustomerDevices = action.payload
    },
    setSelectedDevicesForPerformanceTest: (
      state,
      action: PayloadAction<{ isImmediate?: boolean; devices: ApiTypes.Model.PerformanceTestDevice[] }>,
    ) => {
      state.selectedDevicesForPerformanceTest = action.payload
    },
    setPerformanceTestReport: (state, action: PayloadAction<UITypes.PerformanceTestReport | undefined>) => {
      state.performanceTestReport = action.payload
    },
    setSelectedDevices: (state, action: PayloadAction<ApiTypes.Model.VendorDevice[]>) => {
      state.selectedDevices = action.payload
    },
    setDeviceHistoryLogID: (state, action: PayloadAction<number | undefined>) => {
      state.deviceHistoryLogID = action.payload
    },
    setWarrantySNs: (state, action: PayloadAction<UITypes.WarrantySNs | undefined>) => {
      state.warrantySNs = action.payload
    },
    setModalContent: (state, action: PayloadAction<UITypes.ModalContent | undefined>) => {
      state.modalContent = { title: action.payload?.title || "", content: action.payload?.content || "" }
    },
    setInitialURL: (state, action: PayloadAction<string | undefined>) => {
      state.initialURL = action.payload
    },
    setUserActiveTheme: (state, action: PayloadAction<ContractorThemeKey>) => {
      setStorageItem(StorageKeys.UserTheme, action.payload)
      state.userActiveTheme = action.payload
    },
    setUserLogo: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        setStorageItem(StorageKeys.UserLogo, action.payload)
      } else {
        removeStorageItem(StorageKeys.UserLogo)
      }
      state.userLogo = action.payload
    },
    setSelectedMessageTemplateID: (state, action: PayloadAction<number | undefined>) => {
      state.selectedMessageTemplateID = action.payload
    },
    setAccountSettingsTab: (state, action: PayloadAction<AccountSettingsTabId | undefined>) => {
      state.accountSettingsTab = action.payload
    },
    setVerticalMenuOpen: (state, action: PayloadAction<boolean>) => {
      setStorageItem(StorageKeys.VerticalMenuOpen, action.payload)
      state.verticalMenuOpen = action.payload
    },
    setVerticalMenuOpenMobile: (state, action: PayloadAction<boolean>) => {
      state.mobileMenuOpen = action.payload
    },
  },
})

export const contractorThemeSelector = (state: RootState) => {
  const userActiveTheme = state.tempData.userActiveTheme

  return ContractorThemes[userActiveTheme]
}

export const userLogoSelector = (state: RootState) => {
  if (!state.tempData.userLogo || state.tempData.userLogo === "null") {
    return null
  }

  return state.tempData.userLogo
}

export const verticalMenuOpenSelector = (state: RootState) => state.tempData.verticalMenuOpen
export const mobileMenuOpenSelector = (state: RootState) => state.tempData.mobileMenuOpen

export const {
  setContractorAccountStatusOptions,
  setCurrentOrderNew,
  setContractor,
  setAdditionalRequestParams,
  setCurrentTemplateID,
  setCurrentZip,
  setEditableWarehouse,
  setEditableUser,
  setSelectedDeviceIDs,
  setSelectedCustomerDevices,
  setSelectedDevicesForPerformanceTest,
  setPerformanceTestReport,
  setSelectedDevices,
  setDeviceHistoryLogID,
  setWarrantySNs,
  setModalContent,
  setInitialURL,
  setUserActiveTheme,
  setUserLogo,
  setSelectedMessageTemplateID,
  setAccountSettingsTab,
  setVerticalMenuOpen,
  setVerticalMenuOpenMobile,
} = tempDataSlise.actions

export default tempDataSlise.reducer
